import { useState } from 'react'
import { User } from '../types'
import { auditService } from '../services/auditService'
import { holdRun, rejectRun, sendBackToFirstApproval, HoldStage } from '../services/paymentHoldService'
import './PaymentHoldControls.css'
import { toast } from './Toast'

/**
 * The "something is wrong with this" half of an approval screen.
 *
 * Approving was the only thing either screen could do, so a problem had no
 * outlet. These three actions all require a reason and all write to the audit
 * log, so a run that stops moving carries the explanation with it.
 */

type Action = 'hold' | 'send_back' | 'reject'

interface Props {
  runId: string
  stage: HoldStage
  user: User
  /** Only the second-release screen can send a run back to the first approver. */
  allowSendBack?: boolean
  disabled?: boolean
  onDone: (message: string) => void
}

const COPY: Record<Action, { title: string; blurb: string; cta: string; placeholder: string }> = {
  hold: {
    title: 'Put this payment run on hold',
    blurb:
      'The run leaves the approval queue and appears under Held runs until someone releases it back or rejects it. Nothing is paid.',
    cta: 'Hold this run',
    placeholder: 'e.g. seller bank details do not match the rego certificate',
  },
  send_back: {
    title: 'Send back to first approval',
    blurb:
      'The run returns to the first approver and all six DD checks are cleared, so they are genuinely re-done rather than carried over.',
    cta: 'Send it back',
    placeholder: 'e.g. PPSR certificate is older than 48 hours, please re-run',
  },
  reject: {
    title: 'Reject this payment run',
    blurb:
      'The run is closed permanently. The deal stays where it is and a new run has to be initiated from the deal workspace if the payment should still happen.',
    cta: 'Reject it',
    placeholder: 'e.g. deal cancelled, vehicle failed inspection',
  },
}

export default function PaymentHoldControls({ runId, stage, user, allowSendBack, disabled, onDone }: Props) {
  const [action, setAction] = useState<Action | null>(null)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const close = () => {
    setAction(null)
    setReason('')
    setError(null)
  }

  const submit = async () => {
    if (!action) return
    if (!reason.trim()) {
      setError('A reason is required — it is what the next person reads.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      if (action === 'hold') {
        await holdRun(runId, stage, reason, user.id)
        await auditService.log(user, 'payment_run_held', { details: { payment_run: runId, stage, reason: reason.trim() } })
        toast.saved()
        onDone('Payment run put on hold. It is listed under Held runs.')
      } else if (action === 'send_back') {
        await sendBackToFirstApproval(runId, reason, user.id)
        await auditService.log(user, 'payment_run_sent_back', { details: { payment_run: runId, reason: reason.trim() } })
        toast.saved()
        onDone('Sent back to the first approver, with the DD checks cleared.')
      } else {
        await rejectRun(runId, reason, user.id)
        await auditService.log(user, 'payment_run_rejected', { details: { payment_run: runId, stage, reason: reason.trim() } })
        toast.saved()
        onDone('Payment run rejected.')
      }
      close()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That did not go through. Nothing was changed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="phc">
      <div className="phc-row">
        <span className="phc-label">Something wrong with this run?</span>
        <div className="phc-buttons">
          <button className="phc-btn" disabled={disabled} onClick={() => setAction('hold')}>
            Hold…
          </button>
          {allowSendBack && (
            <button className="phc-btn" disabled={disabled} onClick={() => setAction('send_back')}>
              Send back…
            </button>
          )}
          <button className="phc-btn danger" disabled={disabled} onClick={() => setAction('reject')}>
            Reject…
          </button>
        </div>
      </div>

      {action && (
        <div className="phc-overlay" role="dialog" onClick={close}>
          <div className="phc-modal" onClick={(e) => e.stopPropagation()}>
            <h4>{COPY[action].title}</h4>
            <p className="phc-blurb">{COPY[action].blurb}</p>

            <label className="phc-field">
              <span>Reason (required)</span>
              <textarea
                rows={3}
                autoFocus
                value={reason}
                placeholder={COPY[action].placeholder}
                onChange={(e) => {
                  setReason(e.target.value)
                  setError(null)
                }}
              />
            </label>

            {error && <div className="phc-error">{error}</div>}

            <div className="phc-actions">
              <button className="phc-btn" onClick={close} disabled={busy}>
                Cancel
              </button>
              <button
                className={`phc-btn ${action === 'reject' ? 'danger solid' : 'solid'}`}
                onClick={submit}
                disabled={busy || !reason.trim()}
              >
                {busy ? 'Saving…' : COPY[action].cta}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
